import { Button, ConfirmProvider, ErrorScreen, ToastProvider, UnpluggedArt } from '@/components';
import { AppLayout } from '@/features/app-shell/AppLayout';
import { ApiClientBridge } from '@/features/auth/ApiClientBridge';
import { AuthProvider } from '@/features/auth/AuthProvider';
import RequireAuth from '@/features/auth/RequireAuth';
import { FirstEntryWelcome } from '@/features/profile/FirstEntryWelcome';
import { WelcomeCelebration } from '@/features/profile/WelcomeCelebration';
import { WelcomeDebugShortcut } from '@/features/profile/WelcomeDebugShortcut';
import { queryClient } from '@/lib/queryClient';
import AdminRoleForm from '@/routes/AdminRoleForm';
import AdminRoles from '@/routes/AdminRoles';
import AdminUserEdit from '@/routes/AdminUserEdit';
import AdminUserInvite from '@/routes/AdminUserInvite';
import AdminUsers from '@/routes/AdminUsers';
import AuthorizeError from '@/routes/AuthorizeError';
import DesktopUpdatesIndex from '@/routes/DesktopUpdatesIndex';
import DeviceDetail from '@/routes/DeviceDetail';
import DevicesIndex from '@/routes/DevicesIndex';
import Forbidden from '@/routes/Forbidden';
import Login from '@/routes/Login';
import MyDevicePage from '@/routes/MyDevice';
import NotFound from '@/routes/NotFound';
import PersonalSettingsPage from '@/routes/PersonalSettingsPage';
import PollDetail from '@/routes/PollDetail';
import PollEditor from '@/routes/PollEditor';
import PollResults from '@/routes/PollResults';
import Polls from '@/routes/Polls';
import ProfilePage from '@/routes/ProfilePage';
import RegisterDevicePage from '@/routes/RegisterDevicePage';
import ReleaseAppleUdids from '@/routes/ReleaseAppleUdids';
import ReleaseDetail from '@/routes/ReleaseDetail';
import ReleaseProductDetail from '@/routes/ReleaseProductDetail';
import ReleaseProductsIndex from '@/routes/ReleaseProductsIndex';
import ReleaseVersionNew from '@/routes/ReleaseVersionNew';
import ReleaseVersionsCompare from '@/routes/ReleaseVersionsCompare';
import ReleaseVersionsIndex from '@/routes/ReleaseVersionsIndex';
import RunnersHistory from '@/routes/RunnersHistory';
import RunnersList from '@/routes/RunnersList';
import RunnersMachineDetail from '@/routes/RunnersMachineDetail';
import RunnersMachines from '@/routes/RunnersMachines';
import RunnersOverview from '@/routes/RunnersOverview';
import RunnersQueue from '@/routes/RunnersQueue';
import SettingsDeviceFileGroups from '@/routes/SettingsDeviceFileGroups';
import Welcome from '@/routes/Welcome';
import { Navigate, Route, type RouteSectionProps, Router } from '@solidjs/router';
import { QueryClientProvider } from '@tanstack/solid-query';
import { ErrorBoundary } from 'solid-js';

const RootRedirect = () => <Navigate href="/welcome" />;

const AppShell = (props: RouteSectionProps) => (
  <RequireAuth>
    <AppLayout {...props}>{props.children}</AppLayout>
  </RequireAuth>
);

// Render crash anywhere under the router: log it and offer a full reload (reset would
// just re-run the same broken render).
const Crashed = (props: { error: unknown }) => {
  console.error(props.error);
  return (
    <ErrorScreen
      art={<UnpluggedArt />}
      title="Something went wrong"
      body="An unexpected error occurred. Reload the page to try again."
      action={
        <Button
          size="lg"
          class="min-w-44 shadow-lg shadow-brand-600/30"
          onClick={() => window.location.reload()}
        >
          Reload page
        </Button>
      }
    />
  );
};

const RouterShell = (props: RouteSectionProps) => (
  <AuthProvider>
    <ApiClientBridge>
      {/* router root, not a page: the welcome plays over whatever is on screen */}
      <FirstEntryWelcome />
      <WelcomeDebugShortcut />
      <WelcomeCelebration />
      <ErrorBoundary fallback={(err) => <Crashed error={err} />}>{props.children}</ErrorBoundary>
    </ApiClientBridge>
  </AuthProvider>
);

export default function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <ToastProvider>
        <ConfirmProvider>
          <Router root={RouterShell}>
            <Route path="/" component={RootRedirect} />
            <Route path="/login" component={Login} />
            <Route path="/authorize-error" component={AuthorizeError} />
            <Route path="/403" component={Forbidden} />
            <Route path="/" component={AppShell}>
              <Route path="/welcome" component={Welcome} />
              <Route path="/devices" component={DevicesIndex} />
              <Route path="/devices/register" component={RegisterDevicePage} />
              <Route path="/devices/me" component={MyDevicePage} />
              <Route path="/devices/:deviceId" component={DeviceDetail} />
              <Route path="/profile" component={ProfilePage} />
              <Route path="/profile/settings" component={PersonalSettingsPage} />
              <Route path="/polls" component={Polls} />
              <Route path="/polls/new" component={PollEditor} />
              <Route path="/polls/:pollId/edit" component={PollEditor} />
              <Route path="/polls/:pollId/results" component={PollResults} />
              <Route path="/polls/:pollId" component={PollDetail} />
              <Route path="/admin/users" component={AdminUsers} />
              <Route path="/admin/users/new" component={AdminUserInvite} />
              <Route path="/admin/users/:userId" component={AdminUserEdit} />
              <Route path="/admin/roles" component={AdminRoles} />
              <Route path="/admin/roles/new" component={AdminRoleForm} />
              <Route path="/admin/roles/:roleId" component={AdminRoleForm} />
              <Route path="/release/versions" component={ReleaseVersionsIndex} />
              <Route path="/release/versions/new" component={ReleaseVersionNew} />
              <Route path="/release/compare" component={ReleaseVersionsCompare} />
              <Route path="/release/versions/:releaseId" component={ReleaseDetail} />
              <Route path="/release/desktop-updates" component={DesktopUpdatesIndex} />
              <Route path="/runners" component={RunnersOverview} />
              <Route path="/runners/machines" component={RunnersMachines} />
              <Route path="/runners/machines/:machineId" component={RunnersMachineDetail} />
              <Route path="/runners/runners" component={RunnersList} />
              <Route path="/runners/queue" component={RunnersQueue} />
              <Route path="/runners/history" component={RunnersHistory} />
              <Route path="/release/products" component={ReleaseProductsIndex} />
              <Route path="/release/apple-udids" component={ReleaseAppleUdids} />
              <Route path="/release/products/:productId" component={ReleaseProductDetail} />
              <Route path="/settings/device-file-groups" component={SettingsDeviceFileGroups} />
            </Route>
            <Route path="*" component={NotFound} />
          </Router>
        </ConfirmProvider>
      </ToastProvider>
    </QueryClientProvider>
  );
}
